import { useAtomValue, useSetAtom } from 'jotai';
import {
  FloorLevelStyle,
  FloorLevelProps,
  AbyssViewerContextProvider,
  CanvasProps,
  CameraType,
} from '@fabric-web/3d-viewer';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
// import { makeUniqueId } from '@apollo/client/utilities';
import { SceneCuboids } from '../../modules/cuboids';

import { TestIds } from '../../../../../cypress/testIds';

import {
  Container,
  ParentContainer,
  ViewerLoadingWrapper,
  ViewerLoadingSpinnerContainer,
} from './styles';
import * as globalState from '@/state';

import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { FLOOR_LEVELS_BBOX_PADDING } from '@/constants';
import {
  poiState,
  usePointOfInterestMarkers,
  useBlisterSphericalClick,
} from '@/components/Analysis/modules/pointOfInterest';
import { usePointCloudMaterials } from '@/components/Analysis/modules/pointCloud';

import { useGetStructureId } from '@/hooks/useGetStructureId';
import { AbyssViewer } from './AbyssViewer';
import { PageLoader } from '@/components/shared/PageLoader';
import { useBlisterPolygons } from '../hooks/useBlisterPolygons';
import { useImageSets } from '../hooks/useImageSets';
import { useBlisterClickMarkers } from '../hooks/useBlisterClickMarkers';
// import { useFocusOnSelectedAssembly } from '../hooks/useFocusOnSelectedAssembly';
// import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { FabricErrorBoundary } from '@/components/shared/FabricErrorBoundary';
import { CustomBlockCuboids } from '../../modules/CustomBlocks/components';
import { usePointCloudInfo } from '@/hooks/usePointCloudInfo';
// import { useCuboidState } from '@/components/Analysis/modules/cuboids/hooks/useCuboidState';
// import { useCuboidDataHandlers } from '@/components/Analysis/modules/cuboids/hooks/useCuboidDataHandlers';
// import { useCuboidStyleHandlers } from '@/components/Analysis/modules/cuboids/hooks/useCuboidStyleHandlers';
import { usePointOfInterestMarkerLabels } from '../../modules/pointOfInterest/PointOfInterestTab/usePointOfInterestMarkerLabels';
import { usePointOfInterestPointer } from '../../modules/pointOfInterest/PointOfInterestTab/usePointOfInterestPointer';
import { FocusOnAssembly } from './FocusOnAssembly';
import { PointOfInterestChecked } from '@/types';

const DEFAULT_CANVAS_PROPS: CanvasProps = {
  style: {
    background: '#263238',
    width: '100%',
    height: '100%',
  },
  id: 'outer-viewer',
  gl: { antialias: true, preserveDrawingBuffer: true },
};

// const defaultSelectedColour = formatColor(DEFAULT_COLORS.selectedColour);

const floorLevelStyle: FloorLevelStyle = {
  color: 0xffffff,
  lineWidth: 0.05,
  opacity: 0.5,
};

const IsolatedAssemblyLoadHelperLazy = lazy(() =>
  import('./IsolatedAssemblyLoadHelper').then(({ IsolatedAssemblyLoadHelper }) => ({
    default: IsolatedAssemblyLoadHelper,
  }))
);

export const ScreenshotViewer = () => {
  // const globalCallbacks = useAtomValue(state.globalCallbacks);
  // const setApplyFilters = useSetAtom(state.applyFilters);
  const recoilInspectionMetadata = useAtomValue(state.inspectionMetadata);

  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  // const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const token = useAtomValue(globalState.auth0TokenState);
  const cameraType = useAtomValue(globalState.cameraType);

  const structureId = useGetStructureId();
  const structureDecks = useAtomValue(state.structureDecks);
  const areDecksVisible = useAtomValue(state.areDecksVisible);
  const setArePoisVisible = useSetAtom(state.arePoisVisible);
  const setAreSphericalsVisible = useSetAtom(state.areSphericalsVisible);
  const selectedSpherical = useAtomValue(state.selectedSpherical);

  const structurePois = useAtomValue(poiState.structurePois);
  const setPoiFilteredByCheckbox = useSetAtom(poiState.poiFilteredByCheckbox);

  // const focalPointsByAssemblyId = useAtomValue(state.focalPointsByAssemblyId);
  const structureRelationships = useAtomValue(state.structureRelationship);
  // const structureLocations = useAtomValue(state.structureLocations);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);

  const blisterToAdd = useAtomValue(poiState.blisterToAdd);

  // useLiveSyncAssemblyPois();

  // const cuboidState = useCuboidState();
  // const dataHandlers = useCuboidDataHandlers();
  // const styleHandlers = useCuboidStyleHandlers();

  // const { setPoiDefectData, getDefectLegendOctreeKeys } = usePoiDefectData();

  const showPointCloud = useAtomValue(state.showPointCloud);

  const nodesPerAssemblyPath = useMemo(
    () => recoilInspectionMetadata?.nodesPerAssemblyPath,
    [recoilInspectionMetadata]
  );

  const [nodesToShow, setNodesToShow] = useState<Set<string> | undefined>(undefined);

  const setFilteredAssemblyIds = useSetAtom(state.filteredAssemblyIds);
  const structureRelationship = useAtomValue(state.structureRelationship);
  useEffect(() => {
    // console.log('filteredAssemblies', filteredAssemblies?.length);
    setFilteredAssemblyIds([...Object.keys(structureRelationship.assemblyIdToAnnotation3dArray)]);
  }, [setFilteredAssemblyIds, structureRelationship]);

  useEffect(() => {
    // disable viewpoints for asssembly screenshot
    setAreSphericalsVisible(false);
    setArePoisVisible(true);
  }, [setAreSphericalsVisible, setArePoisVisible]);

  useEffect(() => {
    // enable all pois
    setPoiFilteredByCheckbox(structurePois as PointOfInterestChecked[]);
  }, [structurePois, setPoiFilteredByCheckbox]);

  // // const [assemblyLazyQuery] = useAssemblyLazyQuery();
  // useEffect(() => {
  //   setApplyFilters(true);
  // }, [setApplyFilters]);

  const cameraProps = useMemo(() => {
    const near = cameraType === CameraType.Orthographic ? -1000 : undefined;
    return { cameraType, near };
  }, [cameraType]);

  const isolatedAssemblyNodesUrl = useMemo<string | undefined>(() => {
    if (!structureRelationships || !nodesPerAssemblyPath) {
      return undefined;
    }
    if (!selectedAssemblyId || selectedSpherical) {
      return undefined;
    }
    if (!structureRelationships.assemblyIdToAssembly3d) {
      return undefined;
    }
    const assembly3d = structureRelationships.assemblyIdToAssembly3d[selectedAssemblyId];
    if (assembly3d === undefined || assembly3d === null) {
      return undefined;
    }
    return getCloudfrontUrl(`${nodesPerAssemblyPath}/${assembly3d}.json`);
  }, [structureRelationships, selectedAssemblyId, selectedSpherical, nodesPerAssemblyPath]);

  useEffect(() => {
    if (!isolatedAssemblyNodesUrl) {
      setNodesToShow(undefined);
    }
  }, [isolatedAssemblyNodesUrl]);

  const { maxIds, onOctreeHierarchyLoaded } = usePointCloudInfo();

  const { material, pointCloudMaterials } = usePointCloudMaterials(maxIds);

  // const { data: assemblyRecommendation } = useGetRecommendationsForAssemblyQuery({
  //   variables: {
  //     assemblyId: selectedAssemblyId!,
  //   },
  //   fetchPolicy: 'network-only',
  //   skip: !selectedAssemblyId,
  // });

  // Temporary workaround for slow loading of bin and image files in Pakistan
  // 1. Get the point cloud and image files
  //     - Download horn-mountain-pd.zip from https://drive.google.com/drive/folders/13hdwkiJqbS0prPZi0WtW21av0UcHTm7h?usp=sharing
  //     - Extract to a folder e.g. C:/ - you should see 2 folders inside "horn-mountain-pd" - "images" with jpg files and "octree" with bin files
  //     - Rename the containing folder to 6298b4cf34349ba5acb519d4
  // 2. Host these files on a server within your own machine
  //     - Download and install LTS version of npm releavant to your OS https://nodejs.org/en/download/
  //     - From a command line run: npm install --global http-server
  //     - From a command line, navigate to the folder horn-mountain-pd
  //     - From within horn-mountain-pd in command line run: http-server --cors='*'
  //     - You should see some output saying Available on: http://127.0.0.1:8080
  // 3. Enable Fabric V2 to load files from your own machine
  //     - Go to Fabric V2 temporary version here, stay on Platforms list page for now - https://d2gplkryt7cldz.cloudfront.net/
  //     - Open Chrome dev tools (usually Ctrl+Shift+i)
  //     - Open Console tab (usually second from the left after Elements)
  //     - Run the following in Console tab (paste this in then hit Enter): localStorage.setItem("files-base-url", "http://localhost:8080")
  //     - Select Horn Mountain PD platform - this should now load point cloud and image files from your local machine

  // To use a http server instead of local machine
  // 1. Set up file/folder structure on http server as follows
  // /6298b4cf34349ba5acb519d4 - horn mountain PD
  //    /images - should contain all the .jpg files for horn mountain PD sphericals
  //    /octree - should contain octree_hierarch.json and .bin files for horn mountain PD
  // /61e7ca258f1a3cc4411029cc - marlin UD - lower priority, once horn mountain (folder above) is working end to end
  //    /images - should contain all the .jpg files for marlin UD sphericals
  //    /octree - should contain octree_hierarch.json and .bin files for marlin UD

  // 2. Enable Fabric V2 to load files from your own machine - this only works for Horn Mountain PD
  //     - Go to Fabric V2 temporary version here and log in, stay on Platforms list page for now - https://d2gplkryt7cldz.cloudfront.net/
  //     - Open Chrome dev tools (usually Ctrl+Shift+i)
  //     - Open Console tab (usually second from the left after Elements)
  //     - Run the following in Console tab and replace http://localhost:8080 with your http server url (paste this in then hit Enter): localStorage.setItem("files-base-url", "http://localhost:8080")
  //     - Select any platform - this should now load point cloud and image files from your local machine

  const octreePointCloudsJsonPath = useMemo(() => {
    if (!showPointCloud) return [];
    const filesBaseUrl = localStorage.getItem('files-base-url');
    if (filesBaseUrl) {
      return [
        {
          path: `${filesBaseUrl}/${structureId}/octree/octree_hierarchy.json`,
          nodesToShow,
          contents: [{ 'part-id': true }, { 'corrosion-id': true }, { 'rusting-id': true }],
          onOctreeHierarchyLoaded,
        },
      ];
    }
    if (recoilInspectionMetadata?.octreeResourcesPath) {
      return [
        {
          path: getCloudfrontUrl(recoilInspectionMetadata.octreeResourcesPath),
          nodesToShow,
          contents: [{ 'part-id': true }, { 'corrosion-id': true }, { 'rusting-id': true }],
          onOctreeHierarchyLoaded,
        },
      ];
    }
    return [];
  }, [structureId, recoilInspectionMetadata, nodesToShow, onOctreeHierarchyLoaded, showPointCloud]);

  const { sphericalImages, rectilinearImages } = useImageSets(structureId);

  // const boxCuboid: CuboidProps | undefined = useMemo(() => {
  //   if (!focalPointsByAssemblyId || !selectedAssemblyName) return undefined;
  //   const focalPointEntry = selectedAssemblyName
  //     ? focalPointsByAssemblyId[selectedAssemblyName]
  //     : undefined;

  //   if (!focalPointEntry) return undefined;
  //   const { box } = focalPointEntry;
  //   if (!box) return undefined;

  //   const transform = transformFromAlignedBox({
  //     min: [box.min.x, box.min.y, box.min.z],
  //     max: [box.max.x, box.max.y, box.max.z],
  //   });

  //   const rgb = 0xff8822;

  //   const style = {
  //     variant: CuboidVariants.LinesAndTransparentMesh,
  //     lineColor: rgb,
  //     lineWidth: 1,
  //     lineOpacity: 0.8,
  //     fillColor: rgb,
  //     fillOpacity: 0.02,
  //   };
  //   return {
  //     ...transform,
  //     style,
  //     id: 'boxCuboid-123',
  //     name: 'boxCuboid-123',
  //   };
  // }, [focalPointsByAssemblyId, selectedAssemblyName]);

  // useEffect(() => {
  //   const governingValueForAssembly =
  //     assemblyRecommendation?.assembly?.recommendation?.governingValue;

  //   if (governingValueForAssembly && selectedAssemblyId) {
  //     setNominatedBlister((current) => {
  //       return {
  //         ...current,
  //         [selectedAssemblyId]: governingValueForAssembly,
  //       };
  //     });
  //   }
  // }, [
  //   assemblyRecommendation?.assembly?.recommendation?.governingValue,
  //   selectedAssemblyId,
  //   setNominatedBlister,
  // ]);

  const floorLevels = useMemo(() => {
    if (structureDecks && areDecksVisible) {
      const mappedFloorLevels: FloorLevelProps[] = structureDecks.map(
        ({ name, cadFloorHeight }) => ({
          name,
          label: {
            color: '#888888',
            fontSize: 16,
          },
          labelOffset: [1, 1, 1],
          zLevel: cadFloorHeight ?? 0,
          style: { ...floorLevelStyle },
          gridCellSize: 8,
          bboxPadding: FLOOR_LEVELS_BBOX_PADDING,
        })
      );
      return mappedFloorLevels;
    }
    return [];
  }, [structureDecks, areDecksVisible]);

  const poiMarkerSets = usePointOfInterestMarkers(selectedSpherical);
  const poiMarkerLabelSets = usePointOfInterestMarkerLabels();
  const poiPointerSet = usePointOfInterestPointer();
  const blisterPolygons = useBlisterPolygons();
  const blisterMarkers = useBlisterClickMarkers();
  const handleBlisterSphericalClick = useBlisterSphericalClick();
  // const navbarHeight = useNavbarHeight();

  const markers = useMemo(() => {
    const actualBlisterMarkers = selectedSpherical && blisterMarkers;
    return actualBlisterMarkers;
  }, [selectedSpherical, blisterMarkers]);

  const htmlMarkers = useMemo(() => {
    return [...poiMarkerLabelSets, ...poiPointerSet];
  }, [poiMarkerLabelSets, poiPointerSet]);

  const outerDivStyle: React.CSSProperties = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      position: 'relative',
      ...((blisterToAdd && blisterToAdd.state === 'WaitingForCenter') ||
      (blisterToAdd && blisterToAdd.state === 'WaitingForEdge') ||
      (editPointOfInterest && editPointOfInterest.state === 'WaitingForPoint')
        ? { cursor: 'crosshair' }
        : {}),
    }),
    [blisterToAdd, editPointOfInterest]
  );

  return (
    <>
      {!recoilInspectionMetadata && (
        <ViewerLoadingWrapper>
          <ViewerLoadingSpinnerContainer navbarHeight={0}>
            <PageLoader background="dark" />
          </ViewerLoadingSpinnerContainer>
        </ViewerLoadingWrapper>
      )}

      {recoilInspectionMetadata && token && (
        <ParentContainer navbarHeight={0}>
          <Container data-test-id={TestIds.PLATFORM_EXPLORER_INSIGHTS}>
            <div style={outerDivStyle} tabIndex={1}>
              <FabricErrorBoundary logToSentry>
                <AbyssViewerContextProvider
                  canvasProps={DEFAULT_CANVAS_PROPS}
                  cameraProps={cameraProps}
                >
                  <FocusOnAssembly />
                  <AbyssViewer
                    octreePointCloudsJsonPath={octreePointCloudsJsonPath}
                    pointCloudMaterials={pointCloudMaterials}
                    sphericalImages={sphericalImages}
                    rectilinearImages={rectilinearImages}
                    onSphericalViewClick={handleBlisterSphericalClick}
                    svgMarkerSets={poiMarkerSets}
                    floorLevels={floorLevels}
                    // measurementLines={measurements}
                    material={material}
                    // onPointCloudClick={onPointCloudClick}
                    // onModelClick={onModelClick}
                    polygons={blisterPolygons}
                    markers={markers}
                    htmlMarkerSets={htmlMarkers}
                  >
                    <SceneCuboids />
                    <CustomBlockCuboids />
                    {/* {boxCuboid && <Cuboid {...boxCuboid} />} */}
                    {/* <PhotogrammetryTileset onClick={onModelClick} /> */}
                  </AbyssViewer>

                  {isolatedAssemblyNodesUrl && (
                    <Suspense>
                      <IsolatedAssemblyLoadHelperLazy
                        assemblyNodesUrl={isolatedAssemblyNodesUrl}
                        setNodesToShow={setNodesToShow}
                      />
                    </Suspense>
                  )}
                </AbyssViewerContextProvider>
              </FabricErrorBoundary>
            </div>
            {/* {selectedSpherical && (
              <PictureInPictureContainer
                openState={drawerLeft?.visibility}
                horizontalOffset={leftDrawerWidth}
              >
                <Suspense>
                  <MapLocationLazy />
                </Suspense>
              </PictureInPictureContainer>
            )} */}
            {/* {selectedAssemblyId && userCanUpdateAsset && (
              <Suspense>
                <CreatePoiWidgetLazy />
              </Suspense>
            )} */}
          </Container>
        </ParentContainer>
      )}
    </>
  );
};
